import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  School as SchoolIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const CurriculumSummaryModal = ({ open, onClose, qualificationId, onAnalyze }) => {
  const [loading, setLoading] = useState(false);
  const [curriculumSummary, setCurriculumSummary] = useState(null);
  const [error, setError] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamEvents, setStreamEvents] = useState([]); // [{event, data}]
  const [streamNodes, setStreamNodes] = useState([]); // collected emit_requirement_node calls
  const logRef = useRef(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setStreaming(true);
    setError(null);
    setCurriculumSummary(null);
    setStreamEvents([]);
    setStreamNodes([]);

    try {
      // Force reset on each analyze to ensure fresh results
      try {
        await fetch(`/api/qualifications/${qualificationId}/analyze-curriculum/reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch {}

      const token = localStorage.getItem('token');

      const pushEvent = (event, dataObj) => {
        setStreamEvents(prev => [...prev, { event, data: dataObj }]);
        if (event === 'tool_call' && dataObj && dataObj.arguments) {
          setStreamNodes(prev => [...prev, dataObj.arguments]);
        }
        if (logRef.current) {
          requestAnimationFrame(() => {
            logRef.current.scrollTop = logRef.current.scrollHeight;
          });
        }
      };

      // Prefer EventSource for reliable SSE delivery
      const esUrl = token
        ? `/api/qualifications/${qualificationId}/analyze-curriculum/stream?token=${encodeURIComponent(token)}`
        : `/api/qualifications/${qualificationId}/analyze-curriculum/stream`;
      const es = new EventSource(esUrl);
      const safeParse = (str) => { try { return JSON.parse(str); } catch { return { raw: str }; } };
      es.onmessage = (e) => pushEvent('message', safeParse(e.data));
      es.addEventListener('start', (e) => pushEvent('start', safeParse(e.data)));
      es.addEventListener('debug', (e) => pushEvent('debug', safeParse(e.data)));
      es.addEventListener('status', (e) => pushEvent('status', safeParse(e.data)));
      es.addEventListener('tool_call', (e) => {
        const obj = safeParse(e.data);
        pushEvent('tool_call', obj);
        if (obj && obj.arguments) {
          setStreamNodes(prev => [...prev, obj.arguments]);
        }
      });
      es.addEventListener('tool_call_delta', (e) => {
        const obj = safeParse(e.data);
        pushEvent('tool_call_delta', obj);
        if (obj && obj.nodes) setStreamNodes(obj.nodes);
      });
      es.addEventListener('final_json', (e) => {
        const obj = safeParse(e.data);
        pushEvent('final_json', obj);
        if (obj && obj.nodes) setStreamNodes(obj.nodes);
      });
      es.addEventListener('done', (e) => {
        pushEvent('done', safeParse(e.data));
        es.close();
        setStreaming(false);
        setLoading(false);
      });
      es.onerror = () => {
        pushEvent('error', { message: 'EventSource error' });
        es.close();
        setStreaming(false);
        setLoading(false);
      };
      return;

      // Legacy fetch streaming fallback (dead code after return but kept for reference)
      /*
      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      const parseSSE = (chunk) => {
        buffer += chunk;
        const parts = buffer.split(/\n\n/);
        buffer = parts.pop();
        for (const block of parts) {
          const lines = block.split(/\n/);
          let event = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith(':')) {
              // comment/keepalive
              continue;
            } else if (line.startsWith('event:')) {
              event = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim());
            }
          }
          let dataObj = null;
          const dataStr = dataLines.join('\n');
          try { dataObj = dataStr ? JSON.parse(dataStr) : null; } catch { dataObj = { raw: dataStr }; }
          pushEvent(event, dataObj);
          // Also surface debug/status in console for visibility
          if (event === 'debug' || event === 'status' || event === 'error') {
            // eslint-disable-next-line no-console
          }
          if (event === 'final_json' && dataObj && dataObj.nodes) {
            setStreamNodes(dataObj.nodes);
          }
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        parseSSE(decoder.decode(value, { stream: true }));
      }
      if (buffer.trim()) parseSSE('\n\n');
      */
    } catch (err) {
      setError(err.message || 'Failed to analyze curriculum (stream)');
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const renderCategorySection = (title, items, icon, color, totalCredits) => {
    if (!items || items.length === 0) return null;

    return (
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {React.cloneElement(icon, { sx: { color, mr: 1 } })}
          <Typography variant="h6" sx={{ color }}>
            {title}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>
              Total Credits:
            </Typography>
            <Chip 
              label={totalCredits} 
              size="small" 
              sx={{ backgroundColor: color, color: 'white' }}
            />
          </Box>
        </Box>
        <Grid container spacing={2}>
          {items.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Paper elevation={1} sx={{ p: 1.5, border: `1px solid ${color}20` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color }}>
                    {item.code}
                  </Typography>
                  <Chip 
                    label={`${item.credits} cr`} 
                    size="small" 
                    variant="outlined"
                    sx={{ borderColor: color, color }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 1, minHeight: '2.5em' }}>
                  {item.title}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const hasPct = item && item.percentage !== undefined && item.percentage !== null && !Number.isNaN(Number(item.percentage));
                      const pct = hasPct
                        ? Number(item.percentage)
                        : (totalCredits ? ((item.credits || 0) / (totalCredits || 1)) * 100 : null);
                      return pct !== null && pct !== undefined && !Number.isNaN(pct) ? `${pct.toFixed(1)}% of total` : `—`;
                    })()}
                  </Typography>
                  <TrendingUpIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  const renderAIFormat = (curriculumData) => {
    const totalCredits = curriculumData.total_credits || 0;
    let knowledgeModules = [];
    let practicalModules = [];
    let workExpModules = [];
    let assessmentModules = [];
    if (curriculumData.modules) {
      knowledgeModules = curriculumData.modules.KnowledgeModules ? curriculumData.modules.KnowledgeModules.modules || [] : [];
      practicalModules = curriculumData.modules.PracticalSkillsModules ? curriculumData.modules.PracticalSkillsModules.modules || [] : [];
      workExpModules = curriculumData.modules.WorkExperienceModules ? curriculumData.modules.WorkExperienceModules.modules || [] : [];
      assessmentModules = curriculumData.modules.AssessmentModules ? curriculumData.modules.AssessmentModules.modules || [] : [];
    } else if (curriculumData.components) {
      const components = curriculumData.components;
      knowledgeModules = components.knowledge_based_modules || [];
      practicalModules = components.practical_work_requirements || [];
      workExpModules = components.workplace_experience_requirements || [];
      assessmentModules = components.assessment_requirements || [];
    } else {
      knowledgeModules = curriculumData.knowledge_modules || curriculumData.Knowledge_Modules || [];
      practicalModules = curriculumData.practical_skills_modules || curriculumData.Practical_Skills_Modules || [];
      workExpModules = curriculumData.work_experience_modules || curriculumData.Work_Experience_Modules || [];
      assessmentModules = curriculumData.assessment_modules || curriculumData.Assessment_Methodologies || [];
    }
    const knowledgeTotal = knowledgeModules.reduce((sum, item) => sum + (item.credits || 0), 0);
    const practicalTotal = practicalModules.reduce((sum, item) => sum + (item.credits || 0), 0);
    const workExpTotal = workExpModules.reduce((sum, item) => sum + (item.credits || 0), 0);
    const assessmentTotal = assessmentModules.reduce((sum, item) => sum + (item.credits || 0), 0);
    return (
      <Box>
        <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
            Curriculum Summary
          </Typography>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 2 }}>
            {totalCredits} Total Credits
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#4caf50' }}>
                  {knowledgeTotal}
                </Typography>
                <Typography variant="body2">Knowledge</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#ff9800' }}>
                  {workExpTotal}
                </Typography>
                <Typography variant="body2">Workplace</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#2196f3' }}>
                  {assessmentTotal}
                </Typography>
                <Typography variant="body2">Assessment</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#9c27b0' }}>
                  {practicalTotal}
                </Typography>
                <Typography variant="body2">Practical</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detailed Breakdown by Category
        </Typography>
        {renderCategorySection('Knowledge-Based Modules', knowledgeModules, <SchoolIcon />, '#4caf50', knowledgeTotal)}
        {renderCategorySection('Workplace Experience', workExpModules, <WorkIcon />, '#ff9800', workExpTotal)}
        {renderCategorySection('Assessment Requirements', assessmentModules, <AssessmentIcon />, '#2196f3', assessmentTotal)}
        {renderCategorySection('Practical Requirements', practicalModules, <BuildIcon />, '#9c27b0', practicalTotal)}
      </Box>
    );
  };

  const renderSummary = () => {
    if (!curriculumSummary) return null;
    if (curriculumSummary.curriculum_structure) {
      return renderAIFormat(curriculumSummary.curriculum_structure);
    }
    const totalCredits = curriculumSummary.total_credits || 0;
    const breakdown = curriculumSummary.breakdown || {};
    const summary = curriculumSummary.summary || {};
    const knowledgeTotal = summary.knowledge_based_total || (breakdown.knowledge_based ? breakdown.knowledge_based.reduce((sum, item) => sum + (item.credits || 0), 0) : 0);
    const practicalTotal = summary.practical_requirements_total || (breakdown.practical_requirements ? breakdown.practical_requirements.reduce((sum, item) => sum + (item.credits || 0), 0) : 0);
    const workExpTotal = summary.workplace_experience_total || (breakdown.workplace_experience ? breakdown.workplace_experience.reduce((sum, item) => sum + (item.credits || 0), 0) : 0);
    const assessmentTotal = summary.assessment_total || (breakdown.assessment ? breakdown.assessment.reduce((sum, item) => sum + (item.credits || 0), 0) : 0);
    if (!breakdown.knowledge_based && !breakdown.practical_requirements && !breakdown.workplace_experience && !breakdown.assessment) {
      if (curriculumSummary.curriculum) {
        return renderAIFormat(curriculumSummary.curriculum);
      }
      if (curriculumSummary.qualification_summary) {
        return renderAIFormat(curriculumSummary.qualification_summary);
      }
      if (curriculumSummary.modules) {
        return renderAIFormat(curriculumSummary);
      }
      return (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Unexpected data structure received. Please check the console for details.
          </Alert>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Raw Data Structure:</Typography>
            <pre style={{ fontSize: '0.8rem', overflow: 'auto' }}>
              {JSON.stringify(curriculumSummary, null, 2)}
            </pre>
          </Paper>
        </Box>
      );
    }
    return (
      <Box>
        <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
            Curriculum Summary
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', opacity: 0.8 }}>
            (Using Transformed Format)
          </Typography>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 2 }}>
            {totalCredits} Total Credits
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#4caf50' }}>
                  {knowledgeTotal}
                </Typography>
                <Typography variant="body2">Knowledge</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#ff9800' }}>
                  {workExpTotal}
                </Typography>
                <Typography variant="body2">Workplace</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#2196f3' }}>
                  {assessmentTotal}
                </Typography>
                <Typography variant="body2">Assessment</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#9c27b0' }}>
                  {practicalTotal}
                </Typography>
                <Typography variant="body2">Practical</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detailed Breakdown by Category
        </Typography>
        {renderCategorySection('Knowledge-Based Modules', breakdown.knowledge_based || [], <SchoolIcon />, '#4caf50', knowledgeTotal)}
        {renderCategorySection('Workplace Experience', breakdown.workplace_experience || [], <WorkIcon />, '#ff9800', workExpTotal)}
        {renderCategorySection('Assessment Requirements', breakdown.assessment || [], <AssessmentIcon />, '#2196f3', assessmentTotal)}
        {renderCategorySection('Practical Requirements', breakdown.practical_requirements || [], <BuildIcon />, '#9c27b0', practicalTotal)}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            Curriculum Analysis Summary
          </Typography>
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <TrendingUpIcon />}
          >
            {loading ? 'Analyzing...' : 'Analyze Curriculum'}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Streaming live output */}
        {streaming || streamEvents.length > 0 ? (
          <Paper elevation={1} sx={{ p: 1.5, mb: 2, maxHeight: 300, overflow: 'auto', background: '#0f0f12', color: '#e6e6eb', fontFamily: 'monospace' }} ref={logRef}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Live stream {streaming ? '(running...)' : '(completed)'} • Nodes: {streamNodes.length}
            </Typography>
            <Divider sx={{ mb: 1, opacity: 0.2 }} />
            <pre style={{ margin: 0 }}>
{streamEvents.map((e, idx) => {
  const line = JSON.stringify(e);
  return (<div key={idx}>{line}</div>);
})}
            </pre>
          </Paper>
        ) : null}

        {/* Live assembled JSON during stream */}
        {streamNodes && streamNodes.length > 0 && (
          <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Live JSON (building...)</Typography>
            <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: 300 }}>{JSON.stringify(streamNodes, null, 2)}</pre>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={60} />
          </Box>
        ) : curriculumSummary ? (
          renderSummary()
        ) : (streamNodes && streamNodes.length > 0) ? (
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Final JSON (from stream)</Typography>
            <pre style={{ fontSize: '0.85rem', maxHeight: 400, overflow: 'auto' }}>{JSON.stringify(streamNodes, null, 2)}</pre>
          </Paper>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No curriculum analysis available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Analyze Curriculum" to generate a comprehensive breakdown of qualification codes, credits, and percentages.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CurriculumSummaryModal;
